// Module 11 — the OBJECT INSPECTOR view-model (pure; no DOM, no uPlot). Given the current pick,
// the STATIC model (threaded in by the app via the non-contract makeDashboards seam) and the live
// Snapshot, it builds: (a) titled attribute sections covering EVERYTHING the v3 restoration carries
// — node identity, hosted generators (status/Pmin/ramp/storage/η/CO₂/zone/…), neighbourhood loads
// (demand model, weather sensitivity, demographics, prosumer economics) and edge electrical data —
// plus the LIVE runtime values (output, flow + true direction, loading, SoC, served); and (b) the
// per-object time-series spec the selection mini-graph plots. Boundary: domain only.
import type { GridNode, GridEdge, Load, Generator, Snapshot, Command } from "../domain/index";
import { asEdgeId } from "../domain/index";

export interface InspectorModel {
  nodes: readonly GridNode[];
  edges: readonly GridEdge[];
  loads: readonly Load[];
  generators: readonly Generator[];
}

export interface InspectorSection {
  title: string;
  rows: [string, string][];
}

export interface InspectorSeriesSpec {
  labels: string[]; // 1–2 series the mini-graph plots for this object
  values: (snap: Snapshot) => number[]; // sampled once per new snapshot
}

type Sel = { kind: "node" | "edge" | "gen" | "load"; id: string } | null | undefined;

const fmt = (n: number | undefined, unit = "", digits = 1): string =>
  n === undefined || Number.isNaN(n) ? "—" : `${Number(n.toFixed(digits))}${unit}`;
const pct = (n: number | undefined): string => (n === undefined ? "—" : `${Math.round(n * 100)}%`);

// The merged V4 supergraph carries edge `kv` in MIXED units — V3-sourced transmission lines store VOLTS
// (500000, 230000, 115000, 27600) while Debug-3 lines store kV (500, 230, 115, 27.6). Node `nominalKv` is
// PD (2026-07-02): the kvNorm display shim is RETIRED (commit-or-cut). edge.kv is now kV-coded BY
// CONSTRUCTION — the V4 chain normalizes at its conform.mjs emit boundary (guard.mjs normKv) and the
// artifact gate (reduced_artifact_parity.test.ts) asserts no kv>1000 can ship — so the defensive
// runtime ÷1000 became permanently-dead code. Raw e.kv is used directly below.

function push(rows: [string, string][], k: string, v: string | undefined): void {
  if (v !== undefined && v !== "" && v !== "—") rows.push([k, v]);
}

function generatorSection(g: Generator, snap: Snapshot | undefined): InspectorSection {
  const out = snap?.dispatch.find((d) => String(d.genId) === String(g.id))?.outputMw;
  const rows: [string, string][] = [];
  push(rows, "fuel", g.fuelSubtype ? `${g.fuel} (${g.fuelSubtype})` : g.fuel);
  push(rows, "status", g.status ?? "Operating");
  push(rows, "capacity", fmt(g.capMw, " MW"));
  if (out !== undefined) {
    push(rows, "output NOW", `${fmt(out, " MW")} (${g.capMw > 0 ? Math.round((out / g.capMw) * 100) : 0}% of cap)`);
  }
  push(rows, "min output", g.minOutputMw !== undefined ? fmt(g.minOutputMw, " MW") : undefined);
  push(rows, "ramp", g.rampRateMwPerMin !== undefined ? fmt(g.rampRateMwPerMin, " MW/min") : undefined);
  push(rows, "marginal cost", fmt(g.costPerMwh, " $/MWh"));
  // P13 (demand D): the P11 DELIVERED cost — bare cost grossed up by the transmission-loss factor — with the
  // distance + loss-factor that produced it, so a remote plant's higher effective cost is legible at the unit.
  if (g.deliveredCostPerMwh !== undefined) {
    const lf = g.deliveredCostPerMwh > 0 ? g.costPerMwh / g.deliveredCostPerMwh : 1;
    push(rows, "delivered cost",
      `${fmt(g.deliveredCostPerMwh, " $/MWh")}${g.distanceFromGtaKm !== undefined ? ` (${Math.round(g.distanceFromGtaKm)} km, loss-factor ${lf.toFixed(3)})` : ""}${g.importCorridor ? ` · via ${g.importCorridor}` : ""}`);
  }
  // P13: is THIS plant the price-setter right now? (the engine's marginalUnitId, surfaced at the unit)
  if (snap?.totals.marginalUnitId !== undefined && String(snap.totals.marginalUnitId) === String(g.id)) {
    push(rows, "★ ON MARGIN NOW", `sets λ at $${Math.round(snap.totals.marginalCostPerMwh ?? g.deliveredCostPerMwh ?? g.costPerMwh)}/MWh`);
  }
  push(rows, "merit rank", g.meritRank !== undefined ? String(g.meritRank) : undefined);
  push(rows, "grid role", `${g.gridRole}${g.networkRole ? ` · ${g.networkRole}` : ""}`);
  if (g.storageEnergyMwh !== undefined) {
    // P4 C34: surface the REAL power rating (chargeMw/dischargeMw = the C-rate) alongside energy+duration,
    // so a 2-h and a 4-h battery of equal energy read as different MW here (MW ⟂ MWh, no longer 0.25·cap).
    const rate = g.dischargeMw ?? g.chargeMw;
    push(rows, "storage", `${fmt(g.storageEnergyMwh, " MWh")} / ${fmt(g.storageDurationH, " h")}${rate !== undefined ? ` · ${fmt(rate, " MW")} rate` : ""} · η ${pct(g.roundTripEfficiency)}`);
  }
  push(rows, "heat rate", g.heatRateBtuPerKwh !== undefined ? fmt(g.heatRateBtuPerKwh, " BTU/kWh", 0) : undefined);
  push(rows, "CO₂", g.co2KgPerMwh !== undefined ? fmt(g.co2KgPerMwh, " kg/MWh", 0) : undefined);
  push(rows, "capacity factor", g.capacityFactor !== undefined ? pct(g.capacityFactor) : undefined);
  // P4 C38: the per-unit forced-outage-derated availability (EFORd) — nuclear is unit-specific by refurb.
  // VRE carries none (its availability is the weather CF), so this row appears only for dispatchable units.
  push(rows, "availability (EFORd)", g.availabilityFactor !== undefined ? pct(g.availabilityFactor) : undefined);
  push(rows, "flags", [g.mustRun ? "must-run" : "", g.isChp ? "CHP" : "", g.blackStartCapable ? "black-start" : ""].filter(Boolean).join(" · ") || undefined);
  push(rows, "operator", g.operator);
  push(rows, "IESO zone", g.iesoZone);
  return { title: `⚡ ${g.name}`, rows };
}

function loadSection(l: Load, snap: Snapshot | undefined): InspectorSection {
  const rt = snap?.loads?.find((x) => String(x.id) === String(l.id));
  const pr = snap?.prosumers?.find((x) => String(x.id) === String(l.id));
  const rows: [string, string][] = [];
  push(rows, "kind", l.kind);
  push(rows, "peak demand", fmt(l.maxDemandMw, " MW"));
  if (rt) push(rows, "demand NOW", `${fmt(rt.demandMw, " MW")} · served ${fmt(rt.servedMw, " MW")} · ${rt.status}`);
  push(rows, "criticality", `priority ${l.criticalityPriority}`);
  push(rows, "feeder tier", l.loopType);
  if (l.seasonalScaling) {
    push(rows, "seasonal ×", `w ${l.seasonalScaling.winter} · sp ${l.seasonalScaling.spring} · su ${l.seasonalScaling.summer} · f ${l.seasonalScaling.fall}`);
  }
  if (l.weatherSensSummerMwPerDegC !== undefined) {
    push(rows, "weather sens", `☀ ${fmt(l.weatherSensSummerMwPerDegC, " MW/°C", 3)} · ❄ ${fmt(l.weatherSensWinterMwPerDegC, " MW/°C", 3)}`);
  }
  if (l.peakHourSummer !== undefined) push(rows, "peak hours", `summer ${l.peakHourSummer}:00 · winter ${l.peakHourWinter}:00`);
  push(rows, "annual energy", l.annualConsumptionGwh !== undefined ? fmt(l.annualConsumptionGwh, " GWh") : undefined);
  push(rows, "load factor", l.loadFactorPct !== undefined ? fmt(l.loadFactorPct, "%", 0) : undefined);
  if (l.population !== undefined) {
    push(rows, "population", `${Math.round(l.population).toLocaleString("en-CA")} (${fmt(l.populationDensityPerKm2, "/km²", 0)} · ${fmt(l.areaKm2, " km²", 2)})`);
  }
  if (l.kind === "prosumer") {
    push(rows, "solar", `${fmt(l.peakSolarMw, " MWp")} · roof suitability ${pct(l.rooftopSuitabilityScore)}`);
    push(rows, "battery", `${fmt(l.batteryCapacityMwh, " MWh")} ${l.batteryChemistry ?? ""} · η ${pct(l.batteryEtaAcAc)}`);
    push(rows, "sell offer", l.sellOfferPriceCkWh !== undefined ? fmt(l.sellOfferPriceCkWh, " ¢/kWh") : undefined);
    if (pr) {
      push(rows, "battery NOW", `SoC ${Math.round(pr.socFrac * 100)}% · ${pr.batteryMw > 0 ? "discharging" : pr.batteryMw < 0 ? "charging" : "idle"} ${fmt(Math.abs(pr.batteryMw), " MW")}`);
      push(rows, "solar NOW", `${fmt(pr.solarMw, " MW")} · exporting ${fmt(pr.exportMw, " MW")}`);
    }
  }
  return { title: `🏘 ${l.name}`, rows };
}

// ── STATION GROUPING (node coherence, 2026-06-29) ────────────────────────────────────────────────────
// The Debug-3 model splits each transformer station into a base bus (W<osm>, NAMED) + per-voltage WINDING
// buses (W<osm>_LV###, cls transformer, ANONYMOUS) joined by transformer legs. They are electrically REAL
// distinct voltage buses (we never merge them — that would short the transformers + change golden), but
// they belong to ONE physical station. Grouping them by OSM-core lets the UI present a coherent NAMED
// station from ANY of its buses — the user's "node types for substations (inherited from Debug-3) weren't
// fixed / is V4 a coherent network." Pure + golden-neutral (UI identity only).
const osmCoreOf = (s: string): string => {
  const m = /(\d{4,})/.exec(s.replace(/_(LV|HV|MV)\d.*$/i, "")); // strip a winding suffix, take the OSM id run
  return m ? m[1]! : "";
};
// OSM-core → station name: the named substation/transformer/generator member of a multi-bus core-group.
function stationNameByCore(model: InspectorModel): Map<string, string> {
  const acc = new Map<string, { count: number; name?: string; baseLen: number }>();
  for (const n of model.nodes) {
    const core = osmCoreOf(String(n.id));
    if (!core) continue;
    const e = acc.get(core) ?? { count: 0, baseLen: Infinity };
    e.count++;
    if (n.name && (n.cls === "substation" || n.cls === "transformer" || n.cls === "generator")) {
      const len = String(n.id).length; // the BASE bus has the shortest id (no _LV### suffix) — prefer its name
      if (len < e.baseLen) { e.name = n.name; e.baseLen = len; }
    }
    acc.set(core, e);
  }
  const out = new Map<string, string>();
  for (const [core, e] of acc) if (e.count >= 2 && e.name) out.set(core, e.name);
  return out;
}
// the node's COHERENT display name: its own name, else its station's name + this bus's voltage role, else id.
function displayNodeName(n: GridNode, stations: Map<string, string>): string {
  if (n.name) return n.name;
  const st = stations.get(osmCoreOf(String(n.id)));
  return st ? `${st} · ${Number(n.nominalKv)} kV bus` : String(n.id);
}

// A node's human label for a package datasheet: name + who owns/operates it (Hydro One / an LDC / a
// generator), falling back to its class. Lets the user read WHO each end of a money/info packet is.
function nodeLabel(model: InspectorModel, id: string, stations?: Map<string, string>): string {
  const n = model.nodes.find((x) => String(x.id) === id);
  if (!n) return id;
  const name = displayNodeName(n, stations ?? stationNameByCore(model));
  const who = n.ownerName ?? n.ownerRole ?? n.cls;
  return `${name}${who ? ` · ${who}` : ""}`;
}

// ── per-node-type identity: a distinct icon + human type label so every kind of node reads differently
// (the user's "the UI for several different types of nodes must be fundamentally better per type"). The
// type is decided by what the node ACTUALLY is in the model — a hosted generator ⇒ plant, a hosted load ⇒
// neighbourhood, a transformation/MVA ⇒ transformer station — falling back to its electrical class.
export interface NodeTypeMeta { icon: string; type: string }
export function nodeTypeMeta(n: GridNode, model: InspectorModel): NodeTypeMeta {
  if (n.busType === "slack") return { icon: "★", type: "Slack / reference bus" };
  // a Power plant = a node the data calls a generator OR a bus a dispatch unit attaches to. The `cls` check
  // matters because a named plant's site bus (e.g. Pickering) can carry a switchyard transformation while its
  // unit attaches at a sibling bus — without it the plant would mis-type as a Transformer station.
  if (n.cls === "generator" || model.generators.some((g) => String(g.busId) === String(n.id))) return { icon: "⚡", type: "Power plant" };
  if (model.loads.some((l) => String(l.id) === String(n.id) || String(l.busId) === String(n.id)))
    return { icon: "🏘", type: "Neighbourhood" };
  if (n.cls === "transformer" || n.isTransformerStation || (n.transformation && n.transformation !== "none"))
    return { icon: "🔌", type: "Transformer station" };
  if (n.cls === "switch") return { icon: "🔀", type: "Switch / breaker" };
  if (n.cls === "substation") return { icon: "🏛", type: "Substation" };
  if (n.cls === "junction") return { icon: "•", type: "Junction" };
  return { icon: "▣", type: n.cls };
}

// ── CONNECTED-EDGES view-model (the V3 connections_map.html gold standard, in-app): for a selected NODE,
// every electrical line touching it, with this station's per-line BREAKER (the end of the edge attached to
// THIS node). Each row carries a ready `togglePort` command so the panel can open/close just that one
// connection — the user's "disconnect / connect individual connections like connections_map.html". Pure;
// live values (flow, loading, open/faulted) come from the snapshot, identity from the static model.
export interface ConnectionRow {
  edgeId: string;
  toId: string;
  toName: string; // the OTHER end's human label
  kv: number;
  vTier: string;
  kind: string;
  lengthKm: number;
  rateMva: number;
  isTie: boolean;
  end: "u" | "v"; // which end of the edge is attached to the selected node (the breaker we toggle)
  flowMw: number; // signed: + ⇒ power LEAVES this node toward toId, − ⇒ arrives
  loadingPct: number;
  energized: boolean;
  inService: boolean;
  open: boolean; // this station's breaker on this line is OPEN (disconnected)
  faulted: boolean;
  manualOff: boolean; // the whole line is out of service
  command: Command; // togglePort — flip THIS node's breaker on this line
}

export function connectionRows(sel: Sel, model: InspectorModel | undefined, snap: Snapshot | undefined): ConnectionRow[] {
  if (!sel || sel.kind !== "node" || !model) return [];
  // STATION-AWARE: if the selected bus belongs to a multi-bus station (its _LV### winding family), gather
  // the WHOLE station's EXTERNAL connections (every member bus), excluding the intra-station transformer
  // legs — so clicking any bus of Cherrywood shows the station's full connectivity, not just one voltage.
  const stations = stationNameByCore(model);
  const core = osmCoreOf(sel.id);
  const members = core && stations.has(core)
    ? new Set(model.nodes.filter((n) => osmCoreOf(String(n.id)) === core).map((n) => String(n.id)))
    : new Set([sel.id]);
  const rows: ConnectionRow[] = [];
  for (const e of model.edges) {
    const u = String(e.u);
    const v = String(e.v);
    const uIn = members.has(u);
    const vIn = members.has(v);
    if (!uIn && !vIn) continue; // doesn't touch this station
    if (uIn && vIn) continue; // INTRA-station (a transformer leg / internal bus tie) — not an external line
    const eid = String(e.id);
    if (eid.includes("→")) continue; // synthetic info/finance overlay arc — not an electrical connection
    const end: "u" | "v" = uIn ? "u" : "v";
    const otherId = end === "u" ? v : u;
    const bf = snap?.branchFlows.find((x) => String(x.edgeId) === eid);
    // branchFlow.flowMw is signed +u→v; re-sign so + always means "leaving the station".
    const flowOut = bf ? (end === "u" ? bf.flowMw : -bf.flowMw) : 0;
    const open = snap?.openPorts?.some((p) => p === `${eid}|${end}`) ?? false;
    const manualOff = snap?.manualOffEdges?.some((x) => String(x) === eid) ?? false;
    const faulted = snap?.faultsActive.some((f) => f.targetKind === "branch" && f.targetId === eid) ?? false;
    rows.push({
      edgeId: eid, toId: otherId, toName: nodeLabel(model, otherId, stations), kv: e.kv, vTier: e.vTier, kind: e.kind,
      lengthKm: e.lengthKm, rateMva: e.rateMva, isTie: !!e.isTie, end, flowMw: flowOut,
      loadingPct: bf?.loadingPct ?? 0, energized: bf?.energized === 1, inService: bf?.inService === 1,
      open, faulted, manualOff,
      command: { t: "togglePort", id: asEdgeId(eid), end, closed: open },
    });
  }
  // backbone-first: highest voltage, then most heavily loaded — the lines that matter at the top.
  rows.sort((a, b) => b.kv - a.kv || b.loadingPct - a.loadingPct);
  return rows;
}

// ── facility-wide toggle (the "Turn off" reaches the WHOLE physical facility) ─────────────────────────
// A "substation" is a family of per-voltage buses (W<osm> + W<osm>_LV###) and a "plant" injects at a
// separate G_*_SITE / GEN_* bus reached by a gen_interconnect lead (63/151 gens) — so toggling the ONE
// clicked bus misses the windings AND the generator, and the user sees no effect (their "the off button
// has no effect on many substations/plants"). This returns EVERY bus of the facility: the OSM-core station
// family + any generator site bus electrically attached by a gen_interconnect lead. The "Turn off" then
// de-energizes the whole station + shuts its units. Pure; golden-untouched (it only fans out the command).
export function facilityBuses(sel: Sel, model: InspectorModel | undefined): string[] {
  if (!sel || sel.kind !== "node" || !model) return [];
  const core = osmCoreOf(sel.id);
  const stations = stationNameByCore(model);
  const members = new Set<string>([sel.id]);
  if (core && stations.has(core)) {
    for (const n of model.nodes) if (osmCoreOf(String(n.id)) === core) members.add(String(n.id));
  }
  // walk gen_interconnect leads out from the family (to fixpoint, capped) so a plant's separate G_*_SITE
  // injection bus — and any unit sharing it — is included. gen_interconnect edges are gen leads only, so
  // this never spills into the transmission grid.
  for (let pass = 0; pass < 4; pass++) {
    let grew = false;
    for (const e of model.edges) {
      if (e.kind !== "gen_interconnect") continue;
      const u = String(e.u), v = String(e.v);
      if (members.has(u) && !members.has(v)) { members.add(v); grew = true; }
      else if (members.has(v) && !members.has(u)) { members.add(u); grew = true; }
    }
    if (!grew) break;
  }
  // any generator hosted directly on a member bus (covers gens whose busId IS the selected node)
  for (const g of model.generators) if (members.has(String(g.busId))) members.add(String(g.busId));
  return [...members];
}

// ── SINGLE-LINE DIAGRAM (SLD): a per-node-type schematic the inspector draws as ONE SVG string ────────
// The user's "the diagrams for each selected node type need radical changes." A pure DOM-free view-model
// (nodeDiagramSpec) feeds a pure renderer (renderSldSvg); mount.ts injects the string. Reuses connectionRows
// (feeders + per-end breakers), nodeTypeMeta (the 7-way type), raw kV display (artifact kV-coded by construction, PD). The SLD is a
// LIVE VISUAL schematic — all interaction (open/close a breaker) stays in the connections TABLE below it.
export type SldTier = "EHV" | "HV" | "ST" | "MV" | "LV";
export interface SldFeeder {
  edgeId: string; bus: "main" | "hv" | "lv"; kv: number; vTier: string; toName: string;
  breakerOpen: boolean; flowDir: 1 | -1 | 0; flowMw: number; loadingPct: number;
  state: "live" | "idle" | "open" | "faulted" | "manualOff"; isTie: boolean;
}
export interface SldBus { id: "main" | "hv" | "lv"; kv: number; tier: SldTier; energized: boolean; label: string; }
export interface NodeDiagramSpec {
  nodeId: string; type: string; icon: string; title: string; // title is RAW (renderer escapes it)
  service: "energized" | "deEnergized" | "static" | "faulted";
  buses: SldBus[]; feeders: SldFeeder[];
  overflow?: { count: number; tier: SldTier; openCount: number } | undefined;
  transformer?: { ratio?: string | undefined; mva?: number | undefined; zPct?: number | undefined } | undefined;
  source?: { kind: "gen" | "slack"; outputMw?: number | undefined; capMw?: number | undefined; pct?: number | undefined; fuel?: string | undefined; status?: string | undefined; mustRun?: boolean | undefined; isBattery?: boolean | undefined } | undefined;
  slackMw?: number | undefined;
  sink?: { demandMw?: number | undefined; servedMw?: number | undefined; status?: "normal" | "stressed" | "shed" | "dark" | undefined } | undefined;
  der?: { hasSolar: boolean; solarMw?: number | undefined; peakSolarMw?: number | undefined; hasBattery: boolean; socFrac?: number | undefined; batteryMw?: number | undefined; exportMw?: number | undefined } | undefined;
  switchPair?: { aName: string; bName: string; closed: boolean; flowMw: number } | undefined;
  angleDeg?: number | undefined; passThrough?: boolean | undefined; notes: string[];
}

const MAX_FEEDERS = 12;
const TIER_HEX: Record<string, string> = { EHV: "#ff5a5a", HV: "#ff8c1a", ST: "#f0c419", MV: "#5ec8ff", LV: "#9aa3ad" };
const SLD_GREY = "#3a3f4a", SLD_OVER = "#ff3b3b", SLD_FG = "#cfd8dc", SLD_DIM = "#8b95a3", SLD_OK = "#46d07e";
const sldTierHex = (t: string): string => TIER_HEX[t] ?? "#9aa3ad";
const sldLoadHex = (pct: number): string => (pct > 100 ? SLD_OVER : pct >= 60 ? "#f0c419" : SLD_OK);
function kvTier(kv: number): SldTier {
  if (kv >= 230) return "EHV";
  if (kv >= 115) return "HV";
  if (kv >= 44) return "ST";
  if (kv >= 10) return "MV";
  return "LV";
}
// connections_map.html esc() widened with " and ' so the same helper is safe in BOTH text and attributes.
export function escXml(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}
const ellipsize = (s: string, n = 13): string => (s.length > n ? s.slice(0, n - 1) + "…" : s);

export function nodeDiagramSpec(sel: Sel, model: InspectorModel | undefined, snap: Snapshot | undefined): NodeDiagramSpec | null {
  if (!sel || sel.kind !== "node" || !model) return null;
  const n = model.nodes.find((x) => String(x.id) === sel.id);
  if (!n) return null;
  const meta = nodeTypeMeta(n, model);
  const rows = connectionRows(sel, model, snap); // backbone-first, kV-coded by artifact contract, carries breaker state

  const isStatic = snap?.displayOnlyNodes?.some((d) => String(d) === sel.id) ?? false;
  const out = snap?.outages.some((o) => String(o) === sel.id) ?? false;
  const nodeFaulted = snap?.faultsActive.some((f) => f.targetKind === "bus" && String(f.targetId) === sel.id) ?? false;
  const service: NodeDiagramSpec["service"] = nodeFaulted ? "faulted" : isStatic ? "static" : out ? "deEnergized" : "energized";
  const energized = service === "energized";

  // transformer HV/LV split from "230/27.6" etc. (feeders carry artifact-contract kV values)
  const isXfmr = meta.type === "Transformer station";
  let hv: number | undefined, lv: number | undefined;
  if (isXfmr && n.transformation && n.transformation !== "none") {
    const ps = n.transformation.split("/").map((s) => parseFloat(s)).filter((x) => !Number.isNaN(x));
    if (ps.length >= 2) { hv = Math.max(ps[0]!, ps[1]!); lv = Math.min(ps[0]!, ps[1]!); }
  }
  const twoBus = hv !== undefined && lv !== undefined;
  const assignBus = (kv: number): "main" | "hv" | "lv" => (twoBus ? (kv >= (hv! + lv!) / 2 ? "hv" : "lv") : "main");

  const feederState = (c: ConnectionRow): SldFeeder["state"] =>
    c.manualOff ? "manualOff" : c.faulted ? "faulted" : c.open ? "open" : c.inService ? "live" : "idle";
  const allFeeders: SldFeeder[] = rows.map((c) => ({
    edgeId: c.edgeId, bus: assignBus(c.kv), kv: c.kv, vTier: c.vTier, toName: c.toName,
    breakerOpen: c.open, flowDir: Math.sign(c.flowMw) as 1 | -1 | 0, flowMw: Math.abs(c.flowMw),
    loadingPct: c.loadingPct, state: feederState(c), isTie: c.isTie,
  }));
  const feeders = allFeeders.slice(0, MAX_FEEDERS);
  let overflow: NodeDiagramSpec["overflow"];
  if (allFeeders.length > MAX_FEEDERS) {
    const hidden = allFeeders.slice(MAX_FEEDERS);
    const lowest = hidden.reduce((a, b) => (b.kv < a.kv ? b : a), hidden[0]!);
    overflow = { count: hidden.length, tier: kvTier(lowest.kv), openCount: hidden.filter((h) => h.breakerOpen).length };
  }

  const buses: SldBus[] = twoBus
    ? [
        { id: "hv", kv: hv!, tier: kvTier(hv!), energized, label: `${hv} kV` },
        { id: "lv", kv: lv!, tier: kvTier(lv!), energized, label: `${lv} kV` },
      ]
    : meta.type === "Junction" && rows.length === 2
      ? []
      : [{ id: "main", kv: n.nominalKv, tier: (n.vTier as SldTier) ?? kvTier(n.nominalKv), energized, label: `${Number(n.nominalKv)} kV` }];
  const passThrough = meta.type === "Junction" && rows.length === 2;

  // source: hosted generator(s) and/or slack reference (composable — a slack co-located with a gen draws both)
  let source: NodeDiagramSpec["source"];
  let slackMw: number | undefined;
  const gens = model.generators.filter((g) => String(g.busId) === sel.id);
  if (gens.length > 0) {
    const g = gens[0]!;
    const o = snap?.dispatch.find((d) => String(d.genId) === String(g.id))?.outputMw;
    source = {
      kind: "gen", outputMw: o, capMw: g.capMw,
      pct: o !== undefined && g.capMw > 0 ? Math.round((o / g.capMw) * 100) : undefined,
      fuel: g.fuel, status: g.status, mustRun: g.mustRun, isBattery: g.fuel === "BATTERY",
    };
  }
  if (n.busType === "slack") {
    slackMw = snap?.totals.slackMw;
    if (!source) source = { kind: "slack" };
  }

  // sink: hosted load (+ DER for a prosumer)
  let sink: NodeDiagramSpec["sink"];
  let der: NodeDiagramSpec["der"];
  const load = model.loads.find((l) => String(l.id) === sel.id || String(l.busId) === sel.id);
  if (load) {
    const rt = snap?.loads?.find((x) => String(x.id) === String(load.id));
    sink = { demandMw: rt?.demandMw ?? load.maxDemandMw, servedMw: rt?.servedMw, status: rt?.status };
    if (load.kind === "prosumer") {
      const pr = snap?.prosumers?.find((x) => String(x.id) === String(load.id));
      der = {
        hasSolar: (load.peakSolarMw ?? 0) > 0, solarMw: pr?.solarMw, peakSolarMw: load.peakSolarMw,
        hasBattery: (load.batteryCapacityMwh ?? 0) > 0, socFrac: pr?.socFrac, batteryMw: pr?.batteryMw, exportMw: pr?.exportMw,
      };
    }
  }

  // switch: a 2-pole contact ONLY when exactly 2 connected lines (else the bus-fan covers a switchyard)
  let switchPair: NodeDiagramSpec["switchPair"];
  if (meta.type === "Switch / breaker" && feeders.length === 2) {
    const f0 = feeders[0]!, f1 = feeders[1]!;
    const closed = !f0.breakerOpen && !f1.breakerOpen && f0.state !== "manualOff" && f1.state !== "manualOff";
    switchPair = { aName: f0.toName, bName: f1.toName, closed, flowMw: f0.flowMw * f0.flowDir };
  }

  let transformer: NodeDiagramSpec["transformer"];
  if (isXfmr && n.transformation && n.transformation !== "none") {
    transformer = { ratio: n.transformation, mva: n.transformerStationMva, zPct: n.transformerZPct };
  }

  const a = snap?.nodeAngles.get(n.id);
  const angleDeg = a !== undefined ? (a * 180) / Math.PI : undefined;

  const notes: string[] = [];
  if (service === "faulted") notes.push("⚠ FAULTED — isolated");
  else if (isStatic) notes.push("static infrastructure — no live feed");
  else if (out) notes.push("OUT OF SERVICE");
  if (rows.length === 0) notes.push("isolated — no connections");

  return {
    nodeId: sel.id, type: meta.type, icon: meta.icon, title: displayNodeName(n, stationNameByCore(model)), service,
    buses, feeders, overflow, transformer, source, slackMw, sink, der, switchPair, angleDeg, passThrough, notes,
  };
}

// ── renderSldSvg: PURE spec → one self-contained, fully-escaped <svg> string (no DOM, no createElementNS) ──
export function renderSldSvg(spec: NodeDiagramSpec): string {
  const W = 300;
  const dim = spec.service === "deEnergized" || spec.service === "static";
  const fade = dim ? ` opacity="0.5"` : "";
  const p: string[] = [];

  // glyph helpers (all colours inline — innerHTML-injected SVG children don't inherit the app stylesheet)
  const breaker = (x: number, y: number, f: { breakerOpen: boolean; state: string }): string => {
    const s = 9;
    const open = f.breakerOpen || f.state === "open" || f.state === "manualOff";
    const fault = f.state === "faulted";
    const fill = open ? "none" : fault ? SLD_OVER : SLD_OK;
    const stroke = open ? SLD_OVER : fault ? SLD_OVER : "#1c1f24";
    return `<rect x="${x - s / 2}" y="${y - s / 2}" width="${s}" height="${s}" rx="1.5" fill="${fill}" stroke="${stroke}" stroke-width="1.4"/>`;
  };
  const arrow = (x: number, y: number, dir: number, color: string): string => {
    if (dir === 0) return "";
    // +1 = power leaves the bus (points away/down); -1 = arrives (points toward bus/up)
    const pts = dir > 0 ? `${x - 4},${y - 3} ${x + 4},${y - 3} ${x},${y + 4}` : `${x - 4},${y + 4} ${x + 4},${y + 4} ${x},${y - 3}`;
    return `<polygon points="${pts}" fill="${color}"/>`;
  };
  const text = (x: number, y: number, s: string, o: { size?: number; fill?: string; anchor?: string; rot?: number; weight?: number } = {}): string => {
    const { size = 9, fill = SLD_DIM, anchor = "start", rot, weight } = o;
    const tr = rot !== undefined ? ` transform="rotate(${rot} ${x} ${y})"` : "";
    const fw = weight ? ` font-weight="${weight}"` : "";
    return `<text x="${x}" y="${y}" font-size="${size}" fill="${fill}" text-anchor="${anchor}" font-family="ui-monospace,monospace"${fw}${tr}>${s}</text>`;
  };
  const busRect = (x0: number, x1: number, y: number, b: SldBus): string =>
    `<rect x="${x0}" y="${y - 2}" width="${x1 - x0}" height="4" rx="1" fill="${b.energized ? sldTierHex(b.tier) : SLD_GREY}"/>`;
  const leadColor = (f: SldFeeder): string =>
    f.state === "live" ? sldLoadHex(f.loadingPct) : f.state === "faulted" ? SLD_OVER : SLD_GREY;
  const leadDash = (f: SldFeeder): string =>
    f.state === "live" ? "" : f.state === "manualOff" ? ` stroke-dasharray="1 2.5"` : ` stroke-dasharray="3 2"`;

  // a vertical feeder stub hanging DOWN from a bus at (x, busY): lead → breaker → arrow → kV + rotated name
  const stubDown = (f: SldFeeder, x: number, busY: number): string => {
    const tipY = busY + 30;
    const col = leadColor(f);
    const s: string[] = [];
    s.push(`<line x1="${x}" y1="${busY}" x2="${x}" y2="${tipY}" stroke="${col}" stroke-width="1.6"${leadDash(f)}/>`);
    s.push(breaker(x, busY + 11, f));
    if (f.state === "live") s.push(arrow(x, busY + 22, f.flowDir, col));
    s.push(text(x, tipY + 8, `${Math.round(f.kv)}`, { size: 7.5, fill: sldTierHex(f.vTier), anchor: "middle" }));
    s.push(text(x - 2, tipY + 14, escXml(ellipsize(f.toName)), { size: 7.5, fill: dim ? SLD_GREY : SLD_DIM, rot: 90 }));
    return s.join("");
  };

  // header (every type): icon + escaped name (accent) on the left, type label (dim) on the right
  p.push(text(8, 15, `${spec.icon} ${escXml(ellipsize(spec.title, 24))}`, { size: 11, fill: "var(--accent, #ff8c1a)", weight: 700 }));
  p.push(text(W - 8, 15, escXml(spec.type), { size: 8.5, fill: SLD_DIM, anchor: "end" }));

  let H = 150;
  const layoutFan = (busY: number, x0 = 26, x1 = W - 12): { count: number } => {
    const fs = spec.feeders;
    const slots = fs.length + (spec.overflow ? 1 : 0);
    const step = slots > 1 ? (x1 - x0) / (slots - 1) : 0;
    const xAt = (i: number): number => (slots === 1 ? (x0 + x1) / 2 : x0 + i * step);
    const bus = spec.buses[0] ?? { id: "main" as const, kv: 0, tier: "MV" as SldTier, energized: !dim, label: "" };
    p.push(busRect(x0 - 6, x1 + 6, busY, bus));
    p.push(text(x0 - 6, busY - 6, bus.label, { size: 8, fill: bus.energized ? sldTierHex(bus.tier) : SLD_DIM }));
    if (spec.angleDeg !== undefined) p.push(text(x1 + 6, busY - 6, `θ ${spec.angleDeg.toFixed(2)}°`, { size: 7.5, fill: SLD_DIM, anchor: "end" }));
    fs.forEach((f, i) => p.push(stubDown(f, xAt(i), busY)));
    if (spec.overflow) {
      const x = xAt(fs.length);
      const oc = spec.overflow.openCount > 0 ? SLD_OVER : SLD_GREY;
      p.push(`<line x1="${x}" y1="${busY}" x2="${x}" y2="${busY + 18}" stroke="${oc}" stroke-width="1.6" stroke-dasharray="3 2"/>`);
      p.push(text(x, busY + 34, `+${spec.overflow.count}`, { size: 8, fill: oc, anchor: "middle", weight: 700 }));
      p.push(text(x - 2, busY + 40, `${spec.overflow.tier} more`, { size: 7, fill: SLD_DIM, rot: 90 }));
    }
    return { count: slots };
  };

  if (spec.type === "Transformer station" && spec.buses.length === 2) {
    // two stacked bus bars + the IEC two-circle winding between them
    const hvY = 52, lvY = 150, cx = W / 2;
    const hv = spec.buses[0]!, lv = spec.buses[1]!;
    p.push(busRect(20, W - 20, hvY, hv));
    p.push(text(20, hvY - 6, hv.label, { size: 8, fill: hv.energized ? sldTierHex(hv.tier) : SLD_DIM }));
    p.push(busRect(20, W - 20, lvY, lv));
    p.push(text(20, lvY + 16, lv.label, { size: 8, fill: lv.energized ? sldTierHex(lv.tier) : SLD_DIM }));
    // winding (two overlapping circles, always SOLID — never a marching glyph) + lead between buses
    const wc = dim ? SLD_GREY : "var(--accent, #ff8c1a)";
    p.push(`<line x1="${cx}" y1="${hvY}" x2="${cx}" y2="${lvY}" stroke="${wc}" stroke-width="1.6"/>`);
    p.push(`<circle cx="${cx}" cy="93" r="12" fill="none" stroke="${wc}" stroke-width="1.8"/>`);
    p.push(`<circle cx="${cx}" cy="109" r="12" fill="none" stroke="${wc}" stroke-width="1.8"/>`);
    const t = spec.transformer;
    if (t) {
      const lbl = `${t.ratio ?? ""}${t.mva ? ` · ${Math.round(t.mva)} MVA` : ""}${t.zPct ? ` · Z${t.zPct}%` : ""}`;
      p.push(text(cx + 18, 101, escXml(lbl), { size: 8, fill: SLD_FG }));
    }
    // feeders fan UP off HV (above bus) and DOWN off LV (below bus)
    const hvF = spec.feeders.filter((f) => f.bus === "hv"), lvF = spec.feeders.filter((f) => f.bus === "lv");
    const fanAt = (list: SldFeeder[], y: number, up: boolean): void => {
      const x0 = 30, x1 = W - 24, step = list.length > 1 ? (x1 - x0) / (list.length - 1) : 0;
      list.forEach((f, i) => {
        const x = list.length === 1 ? (x0 + x1) / 2 : x0 + i * step;
        const tip = up ? y - 24 : y + 30;
        const col = leadColor(f);
        p.push(`<line x1="${x}" y1="${y}" x2="${x}" y2="${tip}" stroke="${col}" stroke-width="1.6"${leadDash(f)}/>`);
        p.push(breaker(x, up ? y - 10 : y + 11, f));
        if (f.state === "live") p.push(arrow(x, up ? y - 18 : y + 22, up ? -f.flowDir : f.flowDir, col));
        if (!up) { p.push(text(x, tip + 8, `${Math.round(f.kv)}`, { size: 7.5, fill: sldTierHex(f.vTier), anchor: "middle" })); p.push(text(x - 2, tip + 14, escXml(ellipsize(f.toName)), { size: 7.5, fill: SLD_DIM, rot: 90 })); }
        else p.push(text(x, tip - 4, escXml(ellipsize(f.toName, 9)), { size: 7, fill: SLD_DIM, anchor: "middle" }));
      });
    };
    fanAt(hvF, hvY, true);
    fanAt(lvF, lvY, false);
    H = 230;
  } else if (spec.type === "Power plant") {
    // generator circle (~) on a lead up through a step-up winding onto a bus, feeders fan right/down
    const busY = 96, gx = 40, gy = 150;
    const src = spec.source;
    const lit = src && (src.outputMw ?? 0) !== 0 && src.status !== "Retired" && src.status !== "Decommissioned";
    const gcol = lit ? "var(--accent, #ff8c1a)" : SLD_GREY;
    p.push(`<line x1="${gx}" y1="${gy - 15}" x2="${gx}" y2="${busY}" stroke="${gcol}" stroke-width="1.6"/>`);
    // step-up winding (single circle, solid)
    p.push(`<circle cx="${gx}" cy="${(busY + gy - 15) / 2}" r="7" fill="none" stroke="${dim ? SLD_GREY : "var(--accent, #ff8c1a)"}" stroke-width="1.5"/>`);
    p.push(`<circle cx="${gx}" cy="${gy}" r="15" fill="none" stroke="${gcol}" stroke-width="1.8"/>`);
    p.push(`<path d="M${gx - 8},${gy} q4,-6 8,0 q4,6 8,0" fill="none" stroke="${gcol}" stroke-width="1.6"/>`); // the '~'
    if (src) {
      const fuel = escXml(String(src.fuel ?? ""));
      const mw = src.outputMw !== undefined ? `${Math.round(src.outputMw)} MW${src.pct !== undefined ? ` · ${src.pct}%` : ""}` : (src.status ?? "");
      p.push(text(gx, gy + 28, fuel, { size: 7.5, fill: SLD_DIM, anchor: "middle" }));
      p.push(text(gx, gy + 38, escXml(mw), { size: 8, fill: lit ? SLD_OK : SLD_DIM, anchor: "middle" }));
      if (src.mustRun) p.push(text(gx, gy - 20, "base", { size: 7, fill: SLD_DIM, anchor: "middle" }));
      // capacity gauge bar beside the generator
      const gh = 30, frac = src.capMw && src.outputMw !== undefined ? Math.max(0, Math.min(1, src.outputMw / src.capMw)) : 0;
      p.push(`<rect x="${gx + 20}" y="${gy - gh / 2}" width="6" height="${gh}" rx="1" fill="none" stroke="${SLD_DIM}" stroke-width="1"/>`);
      p.push(`<rect x="${gx + 20}" y="${gy + gh / 2 - gh * frac}" width="6" height="${gh * frac}" rx="1" fill="${lit ? SLD_OK : SLD_GREY}"/>`);
    }
    layoutFan(busY, 70, W - 12);
    H = 196;
  } else if (spec.type === "Switch / breaker" && spec.switchPair) {
    // a 2-pole switch: two stubs joined by a hinged contact (down=closed, lifted=open)
    const y = 96, ax = 50, bx = W - 50, sp = spec.switchPair;
    const col = sp.closed ? "var(--accent, #ff8c1a)" : SLD_GREY;
    p.push(`<line x1="20" y1="${y}" x2="${ax}" y2="${y}" stroke="${SLD_FG}" stroke-width="1.6"/>`);
    p.push(`<line x1="${bx}" y1="${y}" x2="${W - 20}" y2="${y}" stroke="${SLD_FG}" stroke-width="1.6"/>`);
    p.push(`<circle cx="${ax}" cy="${y}" r="3" fill="${SLD_FG}"/>`);
    p.push(`<circle cx="${bx}" cy="${y}" r="3" fill="${SLD_FG}"/>`);
    // the contact blade: closed → straight to bx; open → lifted to a gap above bx
    const tipX = sp.closed ? bx : bx - 14, tipY = sp.closed ? y : y - 16;
    p.push(`<line x1="${ax}" y1="${y}" x2="${tipX}" y2="${tipY}" stroke="${col}" stroke-width="2.2"/>`);
    if (sp.closed && sp.flowMw !== 0) p.push(arrow((ax + bx) / 2, y, sp.flowMw > 0 ? 1 : -1, SLD_OK));
    p.push(text((ax + bx) / 2, y - 20, sp.closed ? "CLOSED" : "OPEN", { size: 8, fill: sp.closed ? SLD_OK : SLD_OVER, anchor: "middle", weight: 700 }));
    if (sp.closed && sp.flowMw !== 0) p.push(text((ax + bx) / 2, y + 16, `${Math.abs(Math.round(sp.flowMw))} MW`, { size: 7.5, fill: SLD_DIM, anchor: "middle" }));
    p.push(text(20, y + 28, escXml(ellipsize(sp.aName, 16)), { size: 7.5, fill: SLD_DIM }));
    p.push(text(W - 20, y + 28, escXml(ellipsize(sp.bName, 16)), { size: 7.5, fill: SLD_DIM, anchor: "end" }));
    H = 140;
  } else if (spec.type === "Neighbourhood") {
    // a load triangle (apex down) fed from a short bus; a prosumer adds PV + battery glyphs
    const busY = 60, lx = W / 2;
    const bus = spec.buses[0] ?? { id: "main" as const, kv: 0, tier: "MV" as SldTier, energized: !dim, label: "" };
    p.push(busRect(40, W - 40, busY, bus));
    p.push(text(40, busY - 6, bus.label, { size: 8, fill: bus.energized ? sldTierHex(bus.tier) : SLD_DIM }));
    // feeder(s) into the bus (drawn as small stubs with breakers on the left)
    spec.feeders.slice(0, 3).forEach((f, i) => {
      const x = 56 + i * 22;
      p.push(`<line x1="${x}" y1="${busY}" x2="${x}" y2="${busY - 16}" stroke="${leadColor(f)}" stroke-width="1.5"${leadDash(f)}/>`);
      p.push(breaker(x, busY - 8, f));
      if (f.state === "live") p.push(arrow(x, busY - 2, -f.flowDir, leadColor(f))); // power arrives → toward bus
    });
    // the load triangle
    const st = spec.sink?.status ?? "normal";
    const tcol = st === "dark" ? SLD_GREY : st === "shed" ? SLD_OVER : st === "stressed" ? "#f0c419" : (bus.energized ? sldTierHex(bus.tier) : SLD_GREY);
    const ty = busY + 18;
    p.push(`<line x1="${lx}" y1="${busY}" x2="${lx}" y2="${ty}" stroke="${tcol}" stroke-width="1.6"/>`);
    p.push(`<polygon points="${lx - 13},${ty} ${lx + 13},${ty} ${lx},${ty + 20}" fill="${st === "shed" ? "none" : tcol}" stroke="${tcol}" stroke-width="1.6"/>`);
    if (spec.sink) {
      const lbl = `${Math.round(spec.sink.servedMw ?? 0)}/${Math.round(spec.sink.demandMw ?? 0)} MW · ${st}`;
      p.push(text(lx, ty + 34, escXml(lbl), { size: 8, fill: SLD_FG, anchor: "middle" }));
    }
    // prosumer DER: PV (square + diagonals) left, battery (plates + SoC bar) right
    if (spec.der) {
      const d = spec.der;
      if (d.hasSolar) {
        const px = 40, py = busY + 26, on = (d.solarMw ?? 0) > 0.05;
        p.push(`<rect x="${px}" y="${py}" width="16" height="12" rx="1" fill="none" stroke="${on ? "#f0c419" : SLD_GREY}" stroke-width="1.4"/>`);
        p.push(`<line x1="${px}" y1="${py + 12}" x2="${px + 16}" y2="${py}" stroke="${on ? "#f0c419" : SLD_GREY}" stroke-width="1"/>`);
        p.push(`<line x1="${px + 6}" y1="${py + 12}" x2="${px + 16}" y2="${py + 4}" stroke="${on ? "#f0c419" : SLD_GREY}" stroke-width="1"/>`);
        p.push(text(px + 8, py + 22, "PV", { size: 7, fill: SLD_DIM, anchor: "middle" }));
      }
      if (d.hasBattery) {
        const bx2 = W - 56, by = busY + 26, soc = Math.max(0, Math.min(1, d.socFrac ?? 0));
        p.push(`<rect x="${bx2}" y="${by}" width="16" height="14" rx="1" fill="none" stroke="${SLD_OK}" stroke-width="1.3"/>`);
        p.push(`<rect x="${bx2}" y="${by + 14 - 14 * soc}" width="16" height="${14 * soc}" fill="${SLD_OK}"/>`);
        p.push(text(bx2 + 8, by + 24, `${Math.round(soc * 100)}%`, { size: 7, fill: SLD_DIM, anchor: "middle" }));
      }
    }
    H = 150;
  } else if (spec.type === "Slack / reference bus") {
    // the reference/source symbol (circle + shrinking ground bars) feeding a bus, then a normal fan
    const busY = 84, rx = 30, ry = 50;
    p.push(`<line x1="${rx}" y1="${ry + 14}" x2="${rx}" y2="${busY}" stroke="var(--accent, #ff8c1a)" stroke-width="1.8"/>`);
    p.push(`<circle cx="${rx}" cy="${ry}" r="14" fill="none" stroke="var(--accent, #ff8c1a)" stroke-width="1.8"/>`);
    p.push(`<line x1="${rx - 7}" y1="${ry - 1}" x2="${rx + 7}" y2="${ry - 1}" stroke="var(--accent, #ff8c1a)" stroke-width="1.5"/>`);
    p.push(`<line x1="${rx - 4}" y1="${ry + 3}" x2="${rx + 4}" y2="${ry + 3}" stroke="var(--accent, #ff8c1a)" stroke-width="1.5"/>`);
    p.push(`<line x1="${rx - 2}" y1="${ry + 6}" x2="${rx + 2}" y2="${ry + 6}" stroke="var(--accent, #ff8c1a)" stroke-width="1.5"/>`);
    p.push(text(rx, ry - 20, "ref · θ0", { size: 7.5, fill: SLD_DIM, anchor: "middle" }));
    if (spec.slackMw !== undefined) p.push(text(rx, busY - 8, `${Math.round(spec.slackMw)} MW`, { size: 8, fill: SLD_OK, anchor: "middle" }));
    if (spec.source && spec.source.kind === "gen") {
      // co-located must-run unit: a small generator circle beside the reference
      p.push(`<circle cx="${rx}" cy="${busY + 18}" r="9" fill="none" stroke="var(--accent, #ff8c1a)" stroke-width="1.5"/>`);
      p.push(`<path d="M${rx - 5},${busY + 18} q2.5,-4 5,0 q2.5,4 5,0" fill="none" stroke="var(--accent, #ff8c1a)" stroke-width="1.3"/>`);
      p.push(`<line x1="${rx}" y1="${busY + 9}" x2="${rx}" y2="${busY}" stroke="var(--accent, #ff8c1a)" stroke-width="1.4"/>`);
    }
    layoutFan(busY, 60, W - 12);
    H = 190;
  } else if (passThroughLayout(spec)) {
    // junction degree-2: a clean PASS-THROUGH line with a midpoint dot + one through-arrow
    const y = 70;
    const f0 = spec.feeders[0]!, f1 = spec.feeders[1]!;
    const col = f0.state === "live" || f1.state === "live" ? sldLoadHex(Math.max(f0.loadingPct, f1.loadingPct)) : SLD_GREY;
    p.push(`<line x1="20" y1="${y}" x2="${W - 20}" y2="${y}" stroke="${col}" stroke-width="1.8"${f0.state === "live" ? "" : ` stroke-dasharray="3 2"`}/>`);
    p.push(breaker(60, y, f0));
    p.push(breaker(W - 60, y, f1));
    p.push(`<circle cx="${W / 2}" cy="${y}" r="4" fill="${dim ? SLD_GREY : SLD_FG}"/>`);
    if (f0.state === "live") p.push(arrow(W / 2 + 18, y, f0.flowDir > 0 ? 1 : -1, col));
    p.push(text(20, y + 16, escXml(ellipsize(f0.toName, 14)), { size: 7.5, fill: SLD_DIM }));
    p.push(text(W - 20, y + 16, escXml(ellipsize(f1.toName, 14)), { size: 7.5, fill: SLD_DIM, anchor: "end" }));
    p.push(text(W / 2, y - 10, "junction", { size: 7.5, fill: SLD_DIM, anchor: "middle" }));
    H = 110;
  } else {
    // Substation / Junction-star (default bus fan)
    layoutFan(spec.type === "Junction" ? 56 : 70);
    H = 190;
  }

  // notes / static-infra banner along the bottom
  if (spec.notes.length > 0) p.push(text(8, H - 6, escXml(spec.notes[0]!), { size: 8, fill: spec.service === "deEnergized" || spec.service === "faulted" ? SLD_OVER : SLD_DIM }));

  const title = escXml(`${spec.type}: ${spec.title}`);
  return `<svg viewBox="0 0 ${W} ${H}" width="100%" height="auto" preserveAspectRatio="xMidYMid meet" role="img" font-family="ui-monospace,monospace"><title>${title}</title><g${fade}>${p.join("")}</g></svg>`;
}
function passThroughLayout(spec: NodeDiagramSpec): boolean {
  return spec.passThrough === true && spec.feeders.length === 2;
}

// The PACKAGE datasheet — for a clicked information/finance "packet" between two nodes (a synthetic
// `from→to` flow id). Resolves the live FlowSample(s) for that pair from the snapshot, names both ends,
// and for a finance packet reconstructs the FULL retail bill the payer is settling this tick (the
// commodity → generator, transmission → Hydro One, distribution → LDC 3-leg split). Returns [] when the
// pair carries no packet this tick (caller then falls back to the lean view).
function packageSections(id: string, model: InspectorModel, snap: Snapshot | undefined): InspectorSection[] {
  const arrow = id.indexOf("→");
  const fromId = arrow >= 0 ? id.slice(0, arrow) : id;
  const toId = arrow >= 0 ? id.slice(arrow + 1) : "";
  const flows = (snap?.flows ?? []).filter((f) => String(f.edgeId) === id);
  if (flows.length === 0) return [];
  const isFinance = flows.some((f) => f.layer === "finance");
  const isInfo = flows.some((f) => f.layer === "information");

  const head: [string, string][] = [];
  push(head, "from", nodeLabel(model, fromId));
  push(head, "to", nodeLabel(model, toId));
  push(head, "carries", [isFinance ? "money" : "", isInfo ? "information" : ""].filter(Boolean).join(" + "));
  const sections: InspectorSection[] = [{ title: `✉ Packet ${fromId} → ${toId}`, rows: head }];

  for (const f of flows) {
    const rows: [string, string][] = [];
    if (f.layer === "finance") {
      push(rows, "settlement leg", f.kind ?? "commodity");
      push(rows, "amount", `$${fmt(f.magnitude, "/tick", 2)}`);
    } else {
      push(rows, "channel", "telemetry / signal");
      push(rows, "rate", fmt(f.magnitude, " pkts/tick", 1));
    }
    push(rows, "direction", `${fromId} → ${toId}`);
    sections.push({ title: f.layer === "finance" ? `$ ${f.kind ?? "commodity"}` : "i information", rows });
  }

  if (isFinance && snap) {
    const bill = { commodity: 0, transmission: 0, distribution: 0, p2p: 0 };
    for (const f of snap.flows) {
      if (f.layer !== "finance" || !String(f.edgeId).startsWith(fromId + "→")) continue;
      const k = (f.kind ?? "commodity") as keyof typeof bill;
      if (k in bill) bill[k] += f.magnitude;
    }
    // only a node that pays RETAIL legs has a "bill" — a pure P2P seller/buyer would otherwise show a
    // misleading all-zero retail bill, so gate on the retail legs being present (not on the p2p total).
    const retail = bill.commodity + bill.transmission + bill.distribution;
    if (retail > 0) {
      const rows: [string, string][] = [];
      push(rows, "commodity → generator", `$${fmt(bill.commodity, "", 2)}`);
      push(rows, "transmission → Hydro One", `$${fmt(bill.transmission, "", 2)}`);
      push(rows, "distribution → LDC", `$${fmt(bill.distribution, "", 2)}`);
      if (bill.p2p > 0) push(rows, "P2P trade", `$${fmt(bill.p2p, "", 2)}`);
      push(rows, "total", `$${fmt(retail + bill.p2p, "/tick", 2)}`);
      sections.push({ title: "🧾 Payer's full bill this tick", rows });
    }
  }
  return sections;
}

export function inspectorSections(sel: Sel, model: InspectorModel | undefined, snap: Snapshot | undefined): InspectorSection[] {
  if (!sel || !model) return [];
  const sections: InspectorSection[] = [];
  // a clicked info/finance packet rides a synthetic `from→to` id — render the PACKAGE datasheet
  if (sel.kind === "edge" && sel.id.includes("→")) {
    const pkg = packageSections(sel.id, model, snap);
    if (pkg.length > 0) return pkg;
  }
  if (sel.kind === "node") {
    const n = model.nodes.find((x) => String(x.id) === sel.id);
    if (n) {
      const meta = nodeTypeMeta(n, model);
      const rows: [string, string][] = [];
      push(rows, "type", meta.type);
      push(rows, "voltage", `${fmt(n.nominalKv, " kV")} (${n.vTier})`);
      if (n.busType !== "PQ") push(rows, "bus type", n.busType);
      // live connectivity summary — total lines on this bus + how many are actually IN SERVICE this tick
      // (the detail rides the connections table below; this is the at-a-glance headline).
      const conns = connectionRows(sel, model, snap);
      const live = conns.filter((c) => c.inService).length;
      push(rows, "connections", conns.length > 0 ? `${conns.length} line${conns.length === 1 ? "" : "s"}${snap ? ` · ${live} in service` : ""}` : String(n.degree));
      if (n.transformation && n.transformation !== "none") {
        push(rows, "transformer", `${n.transformation} kV${n.transformerStationMva ? ` · ${fmt(n.transformerStationMva, " MVA")}` : ""}${n.transformerZPct ? ` · Z ${n.transformerZPct}%` : ""}`);
      }
      if (n.connectionKv) push(rows, "connection kV", fmt(n.connectionKv, " kV"));
      push(rows, "operator", n.operator ?? n.ownerName);
      const dispBus = snap?.displayOnlyNodes?.some((d) => String(d) === sel.id);
      const out = snap?.outages.some((o) => String(o) === sel.id);
      push(rows, "service", dispBus ? "static infrastructure (not in solve)" : out === undefined ? undefined : out ? "OUT OF SERVICE" : "energized");
      const angle = snap?.nodeAngles.get(n.id);
      if (angle !== undefined) push(rows, "bus angle", fmt((angle * 180) / Math.PI, "°", 3));
      // F4b (P13): this bus's nodal LMP decomposition when the grid is congested (energy λ + locational
      // congestion; loss component δ_i deferred). Absent on an uncongested tick ⇒ row omitted.
      const lmp = snap?.nodalPrices?.find((p) => String(p.node) === sel.id);
      if (lmp !== undefined) {
        const congTxt = Math.abs(lmp.congestionPerMwh) > 0.005 ? ` · congestion ${lmp.congestionPerMwh >= 0 ? "+" : "−"}$${fmt(Math.abs(lmp.congestionPerMwh), "", 2)}` : "";
        push(rows, "nodal price (LMP)", `$${fmt(lmp.lmpPerMwh, "/MWh", 2)} (energy $${fmt(lmp.energyPerMwh, "", 1)}${congTxt})`);
      }
      sections.push({ title: `${meta.icon} ${displayNodeName(n, stationNameByCore(model))}`, rows });
    }
    for (const g of model.generators) {
      if (String(g.busId) === sel.id) sections.push(generatorSection(g, snap));
    }
    const load = model.loads.find((l) => String(l.id) === sel.id || String(l.busId) === sel.id);
    if (load) sections.push(loadSection(load, snap));
    // ORPHAN FOOTPRINT (2026-06-29): the selected node id resolves to nothing in the V4 electrical model —
    // an OSM substation/plant footprint whose bus was merged/renamed (or never modelled). Be HONEST instead
    // of leaving the empty "Click a node…" placeholder + a misleading Turn-off: say it carries no live model.
    if (sections.length === 0) {
      sections.push({
        title: `▣ ${sel.id}`,
        rows: [
          ["status", "static infrastructure footprint"],
          ["note", "not in the V4 electrical model — no simulated bus, connections, or flow"],
        ],
      });
    }
  } else if (sel.kind === "edge") {
    const e = model.edges.find((x) => String(x.id) === sel.id);
    if (e) {
      // Core-A: parallel circuits of one corridor share a corridorId — gather the siblings so the
      // datasheet shows the corridor total + per-circuit loading (an N-1 trip darkens one, the rest ~2×).
      const sibs =
        e.corridorId != null
          ? model.edges
              .filter((x) => x.corridorId === e.corridorId)
              .sort((a, b) => (a.circuitIndex ?? 1) - (b.circuitIndex ?? 1))
          : [e];
      const rows: [string, string][] = [];
      push(rows, "type", `${e.kind}${e.isTie ? " · NORMALLY-OPEN TIE" : ""}${e.isSwitchable ? " · switchable" : ""}`);
      push(rows, "voltage", `${fmt(e.kv, " kV")} (${e.vTier})`);
      if (sibs.length > 1) {
        // Each sibling stores x·C so that C of them in parallel reproduce the corridor's modelled
        // susceptance (and the engine's flow split matches the frozen Debug-3 convention). Show the
        // CORRIDOR AGGREGATE impedance (the real electrical value the solver sees) — never the per-sibling x·C.
        const invX = sibs.reduce((s, x) => s + (x.xPu > 0 ? 1 / x.xPu : 0), 0);
        const invR = sibs.reduce((s, x) => s + (x.rPu > 0 ? 1 / x.rPu : 0), 0);
        const xAgg = invX > 0 ? 1 / invX : e.xPu;
        const rAgg = invR > 0 ? 1 / invR : 0;
        const bAgg = sibs.reduce((s, x) => s + x.bPu, 0);
        const totRate = sibs.reduce((s, x) => s + x.rateMva, 0);
        push(rows, "impedance (corridor)", `R ${fmt(rAgg, " pu", 5)} · X ${fmt(xAgg, " pu", 5)} · B ${fmt(bAgg, " pu", 5)}`);
        push(rows, "corridor", `${sibs.length} parallel circuits · ${fmt(totRate, " MVA")} total`);
        push(rows, "this circuit", `#${e.circuitIndex ?? 1} of ${sibs.length} · ${fmt(e.rateMva, " MVA")}`);
      } else {
        push(rows, "impedance", `R ${fmt(e.rPu, " pu", 5)} · X ${fmt(e.xPu, " pu", 5)} · B ${fmt(e.bPu, " pu", 5)}`);
        push(rows, "rating", `${fmt(e.rateMva, " MVA")} · ${e.circuits} circuit${e.circuits === 1 ? "" : "s"}`);
      }
      push(rows, "conductor", e.conductor);
      if (e.thermalMva) push(rows, "thermal rating", fmt(e.thermalMva, " MVA"));
      if (e.voltageInferred) push(rows, "kV source", "inferred (not OSM-tagged)");
      push(rows, "length", fmt(e.lengthKm, " km", 2));
      push(rows, "endpoints", `${String(e.u)} → ${String(e.v)}`);
      push(rows, "merged from", `${e.origBranchIds.length} Debug 3 branch${e.origBranchIds.length === 1 ? "" : "es"}`);
      // P1 reality fix: HONEST provenance for connectors OSM doesn't map (real service drops + underground
      // cables) vs the old blanket "inferred bridge". All carry realistic per-voltage-class impedance.
      if (e.realCorridor)
        push(rows, "provenance", `real corridor${e.corridorRef ? " " + e.corridorRef : ""}${e.corridorConfidence ? " · " + e.corridorConfidence.toLowerCase() + " confidence" : ""}`);
      else if (e.serviceConnection)
        push(rows, "provenance", "service drop — local TS feeds this neighbourhood (real, OSM-unmapped)");
      else if (e.undergroundInferred)
        push(rows, "provenance", "underground interconnect — real cable, not mapped in OSM");
      else if (e.spurConnector)
        push(rows, "provenance", "spur tie — real station's 2nd connection (OSM left it one-ended)");
      else if (e.inferredBridge)
        push(rows, "provenance", "inferred connector — anonymous fragment stitch (uncertain)");
      if (e.corridorSource) push(rows, "source", e.corridorSource);
      const bf = snap?.branchFlows.find((x) => String(x.edgeId) === sel.id);
      if (bf) {
        const dir = bf.flowMw >= 0 ? `${String(e.u)} → ${String(e.v)}` : `${String(e.v)} → ${String(e.u)}`;
        push(rows, "flow NOW", `${fmt(Math.abs(bf.flowMw), " MW")} (${dir}) · ${fmt(bf.loadingPct, "%", 0)} loaded`);
      }
      // per-circuit loading across the parallel siblings (N-1: one circuit goes idle, the rest pick up ~2×)
      if (sibs.length > 1 && snap) {
        for (const s of sibs) {
          const sbf = snap.branchFlows.find((x) => String(x.edgeId) === String(s.id));
          if (!sbf) continue;
          const here = String(s.id) === sel.id ? " ◄" : "";
          push(
            rows,
            `circuit #${s.circuitIndex ?? 1}`,
            `${fmt(Math.abs(sbf.flowMw), " MW")} · ${fmt(sbf.loadingPct, "%", 0)}${sbf.energized ? "" : " · idle"}${here}`,
          );
        }
        // N-1 prediction: identical-impedance parallels share flow, so losing one circuit splits its flow
        // equally across the C−1 survivors. Worst post-contingency survivor loading (full engine-side LODF
        // → cascade lands in Phase 4; this is the closed-form preview from the flows already on screen).
        const fr = sibs.map((s) => {
          const sbf = snap.branchFlows.find((x) => String(x.edgeId) === String(s.id));
          return { mw: sbf ? Math.abs(sbf.flowMw) : 0, rate: s.rateMva > 0 ? s.rateMva : 1 };
        });
        const C = fr.length;
        let worst = 0;
        for (const a of fr)
          for (const b of fr) {
            if (a === b) continue;
            const post = (a.mw + b.mw / (C - 1)) / a.rate;
            if (post > worst) worst = post;
          }
        // The equal-split closed-form is EXACT for identical-impedance siblings (ONT splits) but only an
        // ESTIMATE for real Debug-3 parallels with heterogeneous xPu (maxXpuRatioSiblings>1; the exact
        // inverse-impedance LODF is Phase 4). Label accordingly.
        const het = e.maxXpuRatioSiblings ?? 1;
        push(rows, het > 1.02 ? "N-1 (lose 1, est.)" : "N-1 (lose 1)",
          `worst survivor → ${fmt(worst * 100, "%", 0)}${worst > 1 ? " ⚠ overload" : ""}`);
      }
      const faulted = snap?.faultsActive.some((f) => f.targetKind === "branch" && f.targetId === sel.id);
      if (faulted) push(rows, "state", "⚠ FAULTED — isolated");
      sections.push({ title: `── ${sel.id}`, rows });
    }
  }
  return sections;
}

// The per-object live mini-graph: which 1–2 series to plot for the current pick.
export function inspectorSeries(sel: Sel, model: InspectorModel | undefined): InspectorSeriesSpec | null {
  if (!sel || !model) return null;
  // a clicked packet (synthetic from→to id): graph its magnitude ($ or pkts) over time
  if (sel.kind === "edge" && sel.id.includes("→")) {
    return {
      labels: ["packet magnitude"],
      values: (snap) => [snap.flows.find((x) => String(x.edgeId) === sel.id)?.magnitude ?? 0],
    };
  }
  if (sel.kind === "edge") {
    return {
      labels: ["flow MW", "loading %"],
      values: (snap) => {
        const bf = snap.branchFlows.find((x) => String(x.edgeId) === sel.id);
        return [bf?.flowMw ?? 0, bf?.loadingPct ?? 0];
      },
    };
  }
  if (sel.kind === "node") {
    const gens = model.generators.filter((g) => String(g.busId) === sel.id);
    if (gens.length > 0) {
      return {
        labels: ["output MW"],
        values: (snap) => [
          snap.dispatch
            .filter((d) => gens.some((g) => String(g.id) === String(d.genId)))
            .reduce((a, d) => a + d.outputMw, 0),
        ],
      };
    }
    const load = model.loads.find((l) => String(l.id) === sel.id || String(l.busId) === sel.id);
    if (load && load.kind === "prosumer") {
      return {
        labels: ["SoC %", "solar MW"],
        values: (snap) => {
          const pr = snap.prosumers?.find((x) => String(x.id) === String(load.id));
          return [pr ? pr.socFrac * 100 : 0, pr?.solarMw ?? 0];
        },
      };
    }
    if (load) {
      return {
        labels: ["demand MW", "served MW"],
        values: (snap) => {
          const rt = snap.loads?.find((x) => String(x.id) === String(load.id));
          return [rt?.demandMw ?? 0, rt?.servedMw ?? 0];
        },
      };
    }
    return {
      labels: ["bus angle °"],
      values: (snap) => {
        const a = [...snap.nodeAngles.entries()].find(([k]) => String(k) === sel.id)?.[1];
        return [a !== undefined ? (a * 180) / Math.PI : 0];
      },
    };
  }
  return null;
}
